import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Filter, User } from 'lucide-react';
import SEO from '../components/ui/SEO';
import Lightbox from '../components/ui/Lightbox';
import HowWeWork from '../components/layout/HowWeWork';

interface Project {
  id: number;
  title: string;
  category: string;
  client: string;
  location: string;
  year: string;
  description: string;
  image: string;
  images: string[];
  area: string;
  status: 'Completed' | 'In Progress';
}

const Portfolio: React.FC = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [portfolioRef, portfolioInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [readMoreIndex, setReadMoreIndex] = useState<number | null>(null);

  const filters = [
    { id: 'all', name: 'All Projects' },
    { id: 'residential', name: 'Residential' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'interior', name: 'Interiors' },
    { id: 'landscape', name: 'Landscape' },
  ];

  const completed: Project['status'] = 'Completed';

  /* 🔥 LOCAL IMAGES 1–20 */
  const localImages = Array.from({ length: 20 }, (_, i) => `/images/${i + 1}.jpeg`);

  const projects: Project[] = [
    ...Array.from({ length: 3 }, (_, i) => ({
      id: i + 1,
      title: `Modern Villa Design ${i + 1}`,
      category: 'residential',
      client: `Residential Client ${i + 1}`,
      location: 'Varanasi',
      year: '2024',
      description:
        'A contemporary villa featuring clean lines, large windows, and sustainable design elements.',
      image: localImages[i],
      images: localImages.slice(i * 3, i * 3 + 3),
      area: '3,500 sq ft',
      status: completed,
    })),

    ...Array.from({ length: 3 }, (_, i) => ({
      id: i + 4,
      title: `Office Building ${i + 1}`,
      category: 'commercial',
      client: `Corporate Client ${i + 1}`,
      location: 'Varanasi',
      year: '2023',
      description:
        'A state-of-the-art commercial building with modern amenities and sustainable design.',
      image: localImages[i + 6],
      images: localImages.slice(6 + i * 3, 6 + i * 3 + 3),
      area: '12,000 sq ft',
      status: completed,
    })),

    ...Array.from({ length: 3 }, (_, i) => ({
      id: i + 7,
      title: `Luxury Interior ${i + 1}`,
      category: 'interior',
      client: `Interior Client ${i + 1}`,
      location: 'Varanasi',
      year: '2022',
      description:
        'Elegant interiors with modern aesthetics and functional design.',
      image: localImages[i + 12],
      images: localImages.slice(12 + i * 2, 12 + i * 2 + 3),
      area: '1,200 sq ft',
      status: completed,
    })),

    ...Array.from({ length: 3 }, (_, i) => ({
      id: i + 10,
      title: `Landscape Project ${i + 1}`,
      category: 'landscape',
      client: `Landscape Client ${i + 1}`,
      location: 'Varanasi',
      year: '2025',
      description:
        'Beautifully designed landscapes blending nature and functionality.',
      image: localImages[i + 16],
      images: localImages.slice(16 + i, 16 + i + 3),
      area: '5,000 sq ft',
      status: completed,
    })),
  ];

  const filteredProjects =
    activeFilter === 'all'
      ? projects
      : projects.filter(p => p.category === activeFilter);

  const allImages = projects.flatMap(p => p.images);

  const openLightbox = (imgs: string[], index: number) => {
    const startIndex = allImages.findIndex(img => img === imgs[index]);
    setCurrentImageIndex(startIndex);
    setLightboxOpen(true);
  };

  const nextImage = () => setCurrentImageIndex(i => (i + 1) % allImages.length);
  const prevImage = () => setCurrentImageIndex(i => (i - 1 + allImages.length) % allImages.length);

  return (
    <>
      <SEO title="Portfolio" description="Explore our portfolio of architectural projects" />

      {/* HERO */}
      <section ref={heroRef} className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/1.jpeg" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative z-10 text-center text-white max-w-7xl mx-auto px-6">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            className="text-5xl font-bold mb-6"
          >
            Our <span className="text-yellow-400">Portfolio</span>
          </motion.h1>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-12 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 flex justify-center gap-4 flex-wrap">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`px-6 py-3 rounded-full font-medium transition ${
                activeFilter === f.id
                  ? 'bg-yellow-600 text-white'
                  : 'bg-white dark:bg-gray-800'
              }`}
            >
              <Filter className="inline w-4 h-4 mr-2" />
              {f.name}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section ref={portfolioRef} className="py-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              animate={portfolioInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
              className="rounded-xl overflow-hidden shadow-lg group"
            >
              <div onClick={() => openLightbox(p.images, 0)} className="cursor-pointer">
                <img src={p.image} className="h-64 w-full object-cover group-hover:scale-110 transition" />
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{p.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{p.description}</p>
                <div className="flex justify-between text-sm mb-4">
                  <span className="flex items-center gap-1"><User size={14}/> {p.client}</span>
                  <span>{p.year}</span>
                </div>
                <button
                  onClick={() => setReadMoreIndex(i)}
                  className="w-full bg-yellow-600 text-white py-2 rounded-lg"
                >
                  Read More
                </button>
              </div>

              {readMoreIndex === i && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6">
                  <div className="bg-white rounded-xl max-w-3xl w-full p-6 relative">
                    <button className="absolute top-3 right-4 text-xl" onClick={() => setReadMoreIndex(null)}>×</button>
                    <h3 className="text-2xl font-bold mb-4">{p.title}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {p.images.map((img, idx) => (
                        <img key={idx} src={img} className="h-48 w-full object-cover rounded cursor-pointer"
                          onClick={() => openLightbox(p.images, idx)} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      <Lightbox
        isOpen={lightboxOpen}
        images={allImages}
        currentIndex={currentImageIndex}
        onNext={nextImage}
        onPrevious={prevImage}
        onClose={() => setLightboxOpen(false)}
      />

      <HowWeWork />
    </>
  );
};

export default Portfolio;
